import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Puzzle, Flag, Trash2, Pencil, Mountain } from "lucide-react";
import { toast } from "sonner";
import BulkIO from "@/components/BulkIO";

const FEAT_STATUS = {
  backlog:     { label: "Backlog",     cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  planned:     { label: "Planned",     cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  in_progress: { label: "In progress", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  on_hold:     { label: "On hold",     cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  completed:   { label: "Completed",   cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  cancelled:   { label: "Cancelled",   cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};
const PRIORITIES = ["low", "medium", "high", "critical"];
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function FeatureForm({ open, onOpenChange, projectId, editing, employees, epics, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", owner_id: "", status: "backlog", priority: "medium", start_date: "", due_date: "", epic_id: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(editing
        ? { ...editing, owner_id: editing.owner_id || "", start_date: editing.start_date || "", due_date: editing.due_date || "", epic_id: editing.epic_id || "" }
        : { name: "", description: "", owner_id: "", status: "backlog", priority: "medium", start_date: "", due_date: "", epic_id: "" });
    }
  }, [open, editing]);

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.owner_id) payload.owner_id = null;
      if (!payload.start_date) payload.start_date = null;
      if (!payload.due_date) payload.due_date = null;
      if (!payload.epic_id) payload.epic_id = null;
      if (editing) await api.patch(`/features/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/features`, payload);
      toast.success(editing ? "Feature updated" : "Feature created");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="feature-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Feature" : "New Feature"}</DialogTitle>
          <DialogDescription>A deliverable chunk of work — standalone or inside an Epic.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <UILabel>Name *</UILabel>
            <Input data-testid="feat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <UILabel>Description</UILabel>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <UILabel>Parent Epic</UILabel>
              <Select value={form.epic_id || "none"} onValueChange={(v) => setForm({ ...form, epic_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="feat-epic"><SelectValue placeholder="None (standalone)" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">None (standalone)</SelectItem>
                  {epics.map((e) => <SelectItem key={e.id} value={e.id}>{e.identifier} · {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <UILabel>Owner</UILabel>
              <Select value={form.owner_id || "none"} onValueChange={(v) => setForm({ ...form, owner_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <UILabel>Status</UILabel>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="feat-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FEAT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <UILabel>Priority</UILabel>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><UILabel>Start date</UILabel><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><UILabel>Due date</UILabel><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="feat-submit" onClick={submit} disabled={saving}>{saving ? "Saving…" : (editing ? "Save" : "Create feature")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Features() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [params, setParams] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [features, setFeatures] = useState([]);
  const [epics, setEpics] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [epicFilter, setEpicFilter] = useState("all");

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (!projectId && r.data.length) setProjectId(r.data[0].id); });
    api.get("/employees").then((r) => setEmployees(r.data));
    // eslint-disable-next-line
  }, []);

  const reload = async () => {
    if (!projectId) return;
    const [f, e] = await Promise.all([
      api.get(`/projects/${projectId}/features`).then((r) => r.data),
      api.get(`/projects/${projectId}/epics`).then((r) => r.data),
    ]);
    setFeatures(f); setEpics(e);
    setParams({ project: projectId }, { replace: true });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const canWrite = useMemo(() => {
    const p = projects.find((x) => x.id === projectId);
    return user?.role === "admin" || (p && (p.manager_id === user?.id || (user?.role === "branch_admin" && p?.created_by === user?.id)));
  }, [projects, projectId, user]);

  const filtered = useMemo(() => features.filter((f) => {
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (epicFilter === "standalone" && f.epic_id) return false;
    if (epicFilter !== "all" && epicFilter !== "standalone" && f.epic_id !== epicFilter) return false;
    return true;
  }), [features, statusFilter, epicFilter]);

  const removeFeature = async (f) => {
    if (!window.confirm(`Delete ${f.identifier}? Child tasks stay — their link to this feature is cleared.`)) return;
    try { await api.delete(`/features/${f.id}`); toast.success("Feature deleted"); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="features-header" title="Features" subtitle="Deliverable chunks that make up Epics (or stand alone)"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <BulkIO
              entityLabel="Feature" entityPlural="Features"
              exportUrl={`/api/features/export.csv${projectId ? `?project_id=${projectId}` : ""}`}
              templateUrl="/api/features/import/template.csv"
              importUrl="/features/import"
              columns="name · description · project_code · epic_identifier · status · priority · owner_email · start_date · due_date"
              canImport={user?.role === "admin"}
              onImported={reload}
            />
            {canWrite && projectId && (
              <Button data-testid="new-feat-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" />New Feature
              </Button>
            )}
          </div>
        }
      />

      <SectionCard
        title={`${filtered.length} feature${filtered.length === 1 ? "" : "s"}`}
        subtitle={projectId ? projects.find((p) => p.id === projectId)?.name : "Select a project"}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-9 w-56" data-testid="feat-project-sel"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} <span className="text-muted-foreground">· {p.code}</span></SelectItem>)}</SelectContent>
            </Select>
            <Select value={epicFilter} onValueChange={setEpicFilter}>
              <SelectTrigger className="h-9 w-56" data-testid="feat-epic-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All epics</SelectItem>
                <SelectItem value="standalone">Standalone (no epic)</SelectItem>
                {epics.map((e) => <SelectItem key={e.id} value={e.id}>{e.identifier} · {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="feat-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(FEAT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      >
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No features match your filter. Create one to deliver a chunk of value.</div>
        ) : (
          <div className="space-y-2" data-testid="feat-list">
            {filtered.map((f) => {
              const sm = FEAT_STATUS[f.status] || {};
              const owner = employees.find((emp) => emp.id === f.owner_id);
              const epic = epics.find((e) => e.id === f.epic_id);
              return (
                <div key={f.id} className="glass rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`feat-row-${f.id}`}>
                  <Badge variant="outline" className="text-[10px] font-mono shrink-0">{f.identifier}</Badge>
                  <div className="flex-1 min-w-0">
                    <Link to={`/features/${f.id}`} className="font-medium text-sm hover:text-primary truncate block">{f.name}</Link>
                    <div className="flex items-center gap-2 mt-1">
                      {epic && (
                        <Link to={`/epics/${epic.id}`} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary">
                          <Mountain className="w-2.5 h-2.5" />{epic.identifier}
                        </Link>
                      )}
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden max-w-[220px]"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${f.progress || 0}%` }} /></div>
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{f.progress || 0}% · {f.completed_task_count || 0}/{f.task_count || 0}</span>
                    </div>
                  </div>
                  {owner && <div className="flex items-center gap-1 text-[11px] shrink-0" title={owner.full_name}><Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initialsOf(owner.full_name)}</AvatarFallback></Avatar>{owner.full_name}</div>}
                  <Badge className={`${sm.cls} border text-[10px] shrink-0`}>{sm.label}</Badge>
                  {f.due_date && <Badge variant="outline" className="text-[10px] gap-1 shrink-0"><Flag className="w-2.5 h-2.5" />{f.due_date}</Badge>}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" data-testid={`feat-open-${f.id}`} onClick={() => nav(`/features/${f.id}`)}>Open</Button>
                    {canWrite && (
                      <>
                        <Button size="icon" variant="ghost" data-testid={`feat-edit-${f.id}`} onClick={() => { setEditing(f); setFormOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`feat-del-${f.id}`} onClick={() => removeFeature(f)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <FeatureForm open={formOpen} onOpenChange={setFormOpen} projectId={projectId} editing={editing} employees={employees} epics={epics} onSaved={reload} />
    </div>
  );
}
